import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { API_BASE } from '../api';
import { isSpecialAvatarUser, SpecialAvatar } from './UserBadge';
import formatText from '../formatText';

function timeAgo(dateString) {
  if (!dateString) return '';
  var now = Date.now();
  var then = new Date(dateString).getTime();
  if (isNaN(then)) return '';
  var seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'now';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm';
  var hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h';
  var days = Math.floor(hours / 24);
  if (days < 7) return days + 'd';
  var weeks = Math.floor(days / 7);
  return weeks + 'w';
}

function avatarColor(username) {
  var hash = 0;
  for (var i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  var hue = ((hash % 360) + 360) % 360;
  return 'hsl(' + hue + ', 55%, 55%)';
}

var CardLink = styled(Link)`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  text-decoration: none;
  color: inherit;
  background: white;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

var Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  align-self: center;
  margin-left: 0.75rem;
`;

var AvatarPlaceholder = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.7rem;
  flex-shrink: 0;
  align-self: center;
  margin-left: 0.75rem;
`;

var ContentBlock = styled.div`
  flex: 1;
  min-width: 0;
  align-self: center;
  padding: 0.6rem 0;
`;

var TopLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 0.8rem;
`;

var DisplayName = styled.div`
  font-weight: 700;
  font-size: 0.8rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

var Username = styled.span`
  color: #888;
  font-size: 0.75rem;
  flex-shrink: 0;
`;

var Time = styled.span`
  color: #999;
  font-size: 0.75rem;
  flex-shrink: 0;
`;

var MediaTag = styled.div`
  font-size: 0.7rem;
  color: #999;
  margin-top: 0.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
`;

var PostText = styled.div`
  font-size: 0.8rem;
  color: #333;
  line-height: 1.4;
  margin-top: 0.15rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

var StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.25rem;
  flex-wrap: wrap;
`;

var ReactionPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.7rem;
  color: #999;
`;

var CommentStat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.7rem;
  color: #999;
`;

var Thumbnail = styled.img`
  width: 80px;
  aspect-ratio: 1;
  object-fit: cover;
  flex-shrink: 0;
`;

export default function CompactPostCard(props) {
  var post = props.post;
  var showMediaTag = props.showMediaTag !== false;
  var user = post.user;
  var media = post.media;

  var displayName = user.displayName || user.username;
  // A list has no top-level media, so borrow the first item's artwork as the
  // thumbnail. Feed responses carry `preview`, detail responses carry `items`.
  var listItems =
    post.kind === 'list' && post.list ? post.list.preview || post.list.items || [] : [];
  var rawArtworkURL = media
    ? media.artwork_url
    : listItems.length > 0 && listItems[0].media
      ? listItems[0].media.artwork_url
      : null;
  var artworkURL = rawArtworkURL ? rawArtworkURL.replace('{w}x{h}', '120x120') : null;
  var profilePicURL = user.profilePictureURL ? API_BASE + user.profilePictureURL : null;

  var mediaLabelNode = null;
  if (showMediaTag && post.kind === 'list' && post.list) {
    mediaLabelNode = (
      <div
        style={{
          fontWeight: 900,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {post.list.title}
      </div>
    );
  } else if (showMediaTag && media) {
    var title = media.song_title || media.album_title;
    if (title) {
      mediaLabelNode = (
        <>
          {media.artist && (
            <div
              style={{
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {media.artist}
            </div>
          )}
          <div style={{ fontStyle: 'italic' }}>{title}</div>
        </>
      );
    }
  }

  return (
    <CardLink to={'/post/' + post.id}>
      {isSpecialAvatarUser(user.username) ? (
        <div style={{ flexShrink: 0, alignSelf: 'center', marginLeft: '0.75rem' }}>
          <SpecialAvatar size={28} />
        </div>
      ) : profilePicURL ? (
        <Avatar src={profilePicURL} alt={user.username} />
      ) : (
        <AvatarPlaceholder style={{ background: avatarColor(user.username) }}>
          {user.username.charAt(0).toUpperCase()}
        </AvatarPlaceholder>
      )}
      <ContentBlock>
        {mediaLabelNode && <DisplayName>{mediaLabelNode}</DisplayName>}
        <TopLine>
          <Username>@{user.username}</Username>
          <Time>{timeAgo(post.created_at)}</Time>
        </TopLine>
        {post.text && (
          <PostText
            dangerouslySetInnerHTML={{
              __html: formatText(post.text, post.musicMentions, post.media?.artist),
            }}
          />
        )}
        {(post.reactions?.length > 0 || post.comments?.length > 0) && (
          <StatsRow>
            {post.reactions?.map(function (r) {
              return (
                <ReactionPill key={r.emoji}>
                  {r.emoji} {r.count}
                </ReactionPill>
              );
            })}
            {post.comments?.length > 0 && (
              <CommentStat>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                {post.comments.reduce(function (total, c) {
                  return total + 1 + (c.replies ? c.replies.length : 0);
                }, 0)}
              </CommentStat>
            )}
          </StatsRow>
        )}
      </ContentBlock>
      {artworkURL && <Thumbnail src={artworkURL} alt="artwork" />}
    </CardLink>
  );
}
